import {
  REPERTOIRE_PROGRESS,
  RepertoireActionTypes,
  type UserRepertoireProgress,
} from 'Types/RepertoireTypes';

/**
 * The progress row written when a piece played ungraded stops. It records when
 * the piece was last played and at what tempo, and nothing about how well.
 *
 * - A piece with a row: the row's own status goes back unchanged (the server
 *   requires a status on every write) with the new play time and tempo. The
 *   server merges the write into the row, so accuracy, completion and the
 *   completion date stay what graded play left them.
 * - A piece with no row yet: the row is created in progress with zero accuracy
 *   and completion, the same values a graded first open with no notes writes,
 *   and never complete.
 */
export type UngradedRepertoireProgress = Pick<
  UserRepertoireProgress,
  'status' | 'repertoire' | 'last_played_at' | 'last_tempo'
> &
  Partial<Pick<UserRepertoireProgress, 'accuracy' | 'percent_complete'>>;

export interface UngradedRepertoireProgressInputs {
  repertoireId: number;
  /** The status of the player's row for the piece; null when there is no row. */
  existingStatus: string | null;
  playedAt: Date;
  tempo: number;
}

export const ungradedRepertoireProgressFields = ({
  repertoireId,
  existingStatus,
  playedAt,
  tempo,
}: UngradedRepertoireProgressInputs): UngradedRepertoireProgress => {
  const played = {
    repertoire: { id: repertoireId },
    last_played_at: playedAt.toISOString(),
    last_tempo: tempo,
  };
  return existingStatus !== null
    ? { status: existingStatus, ...played }
    : { status: REPERTOIRE_PROGRESS.IN_PROGRESS, ...played, accuracy: 0, percent_complete: 0 };
};

/** The progress update for a piece played ungraded (same request as graded play's). */
export const ungradedRepertoireProgress = (inputs: UngradedRepertoireProgressInputs) => ({
  type: RepertoireActionTypes.UPDATE_REPERTOIRE_DATA_REQUEST,
  payload: ungradedRepertoireProgressFields(inputs),
});
